import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getHomeTab,
  createHomeTab,
  updateHomeTab,
  TAB_ID_REGEX,
} from "@/services/firebase/homeTab";
import { getTags } from "@/services/firebase/tag";
import { getDramas } from "@/services/firebase/drama";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { HomeTabConfig, HomeTabType } from "@/types/homeTab";
import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
} from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { ArrowLeft, Save, Loader2 } from "lucide-react";

const homeTabSchema = z
  .object({
    id: z
      .string()
      .optional()
      .refine(
        (val) => {
          // 如果提供了 id，則驗證格式
          if (val && val.trim()) {
            return TAB_ID_REGEX.test(val);
          }
          return true;
        },
        {
          message: "Tab ID 只能包含小寫字母、數字和底線",
        }
      ),
    title: z.string().min(1, "標題為必填"),
    icon: z.string().optional(),
    order: z.number().min(0, "順序必須大於等於 0"),
    enabled: z.boolean(),
    type: z.enum(["featured", "ranking", "tag_filter", "custom"]),
    config: z.object({
      tag_ids: z.array(z.string()).optional(),
      drama_ids: z.array(z.string()).optional(),
      limit: z.number().min(1).optional(),
    }),
  })
  .refine(
    (data) => {
      if (data.type === "tag_filter" && (!data.config.tag_ids || data.config.tag_ids.length === 0)) {
        return false;
      }
      return true;
    },
    {
      message: "標籤篩選類型必須至少選擇一個標籤",
      path: ["config", "tag_ids"],
    }
  )
  .refine(
    (data) => {
      if (data.type === "custom" && (!data.config.drama_ids || data.config.drama_ids.length === 0)) {
        return false;
      }
      return true;
    },
    {
      message: "自訂列表類型必須至少選擇一個劇集",
      path: ["config", "drama_ids"],
    }
  )
  .refine(
    (data) => {
      // featured 類型允許自訂輪播，但最多 6 部
      if (data.type === "featured" && data.config.drama_ids && data.config.drama_ids.length > 6) {
        return false;
      }
      return true;
    },
    {
      message: "精選輪播最多只能選擇 6 部劇集",
      path: ["config", "drama_ids"],
    }
  );

type HomeTabFormData = z.infer<typeof homeTabSchema>;

export default function HomeLayoutEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState<string | null>(null);
  const isNew = id === "new" || id === undefined;
  const actualId = isNew ? undefined : id?.replace("/edit", "").replace("edit", "");

  // 載入 Tab 數據
  const { data: tab, isLoading } = useQuery({
    queryKey: ["homeTab", actualId],
    queryFn: () => getHomeTab(actualId!),
    enabled: !isNew && !!actualId,
  });

  // 載入標籤和劇集（用於選擇）
  const { data: tags, isLoading: tagsLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => getTags(),
  });

  const { data: dramas, isLoading: dramasLoading } = useQuery({
    queryKey: ["dramas"],
    queryFn: () => getDramas(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<HomeTabFormData>({
    resolver: zodResolver(homeTabSchema),
    defaultValues: {
      title: "",
      icon: "",
      order: 0,
      enabled: true,
      type: "featured",
      config: {
        tag_ids: [],
        drama_ids: [],
        limit: undefined,
      },
    },
  });

  const tabType = watch("type");
  const selectedTagIds = watch("config.tag_ids") || [];
  const selectedDramaIds = watch("config.drama_ids") || [];
  const enabledValue = watch("enabled");

  const dramaIdByDocId = useMemo(() => {
    const map = new Map<string, string>();
    (dramas || []).forEach((d) => {
      if (d.id && d.drama_id) map.set(d.id, d.drama_id);
    });
    return map;
  }, [dramas]);

  // 當 Tab 類型改變時，清除不相關的 config 欄位
  useEffect(() => {
    if (tabType === "ranking") {
      setValue("config.tag_ids", []);
      setValue("config.drama_ids", []);
    } else if (tabType === "featured") {
      // 精選：允許 config.drama_ids 作為輪播自訂，僅清除 tag_ids
      setValue("config.tag_ids", []);
    } else if (tabType === "tag_filter") {
      setValue("config.drama_ids", []);
    } else if (tabType === "custom") {
      setValue("config.tag_ids", []);
    }
  }, [tabType, setValue]);

  // 向後相容：如果 home_tabs.config.drama_ids 存的是 dramas doc.id，轉成 drama.drama_id
  useEffect(() => {
    if (!dramas || dramas.length === 0) return;
    if (!selectedDramaIds || selectedDramaIds.length === 0) return;

    const normalized = selectedDramaIds
      .map((id) => dramaIdByDocId.get(id) ?? id)
      .filter((id): id is string => Boolean(id && id.trim()));

    // 去重（保留順序）
    const deduped: string[] = [];
    const seen = new Set<string>();
    for (const id of normalized) {
      if (seen.has(id)) continue;
      seen.add(id);
      deduped.push(id);
    }

    // featured 輪播最多 6
    const finalIds = tabType === "featured" ? deduped.slice(0, 6) : deduped;

    const same =
      finalIds.length === selectedDramaIds.length &&
      finalIds.every((v, i) => v === selectedDramaIds[i]);
    if (!same) {
      setValue("config.drama_ids", finalIds, { shouldValidate: true });
    }
  }, [dramas, dramaIdByDocId, selectedDramaIds, setValue, tabType]);

  // 初始化表單數據
  useEffect(() => {
    if (tab && !isNew) {
      setValue("id", tab.id);
      setValue("title", tab.title);
      setValue("icon", tab.icon || "");
      setValue("order", tab.order);
      setValue("enabled", tab.enabled);
      setValue("type", tab.type);
      setValue("config.tag_ids", tab.config.tag_ids || []);
      setValue("config.drama_ids", tab.config.drama_ids || []);
      setValue("config.limit", tab.config.limit);
    }
  }, [tab, setValue, isNew]);

  const mutation = useMutation({
    mutationFn: async (data: HomeTabFormData) => {
      // 準備 config，只包含相關的欄位
      const config: HomeTabConfig = {};
      if (data.type === "tag_filter" && data.config.tag_ids && data.config.tag_ids.length > 0) {
        config.tag_ids = data.config.tag_ids;
      }
      // custom：自訂列表
      // featured：自訂輪播（可選）
      if (
        (data.type === "custom" || data.type === "featured") &&
        data.config.drama_ids &&
        data.config.drama_ids.length > 0
      ) {
        config.drama_ids = data.config.drama_ids;
      }
      if (data.config.limit) {
        config.limit = data.config.limit;
      }

      if (isNew) {
        // 新增模式
        return await createHomeTab({
          id: data.id,
          title: data.title,
          icon: data.icon || undefined,
          order: data.order,
          enabled: data.enabled,
          type: data.type,
          config,
        });
      } else {
        // 編輯模式
        if (!actualId) {
          throw new Error("缺少 Tab ID");
        }
        await updateHomeTab(actualId, {
          title: data.title,
          icon: data.icon || undefined,
          order: data.order,
          enabled: data.enabled,
          type: data.type,
          config,
        });
        return actualId;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homeTabs"] });
      queryClient.invalidateQueries({ queryKey: ["homeTab", actualId] });
      setSaveError(null);
      navigate("/home-layout");
    },
    onError: (error) => {
      console.error("儲存 Tab 失敗:", error);
      setSaveError(error instanceof Error ? error.message : "未知錯誤");
    },
  });

  const onSubmit = (data: HomeTabFormData) => {
    // 確保 id 為小寫（如果提供了）
    const normalizedData = {
      ...data,
      id: data.id ? data.id.toLowerCase() : undefined,
    };
    mutation.mutate(normalizedData);
  };

  const handleTagToggle = (tagId: string) => {
    const current = selectedTagIds;
    const newSelection = current.includes(tagId)
      ? current.filter((id) => id !== tagId)
      : [...current, tagId];
    setValue("config.tag_ids", newSelection, { shouldValidate: true });
  };

  const handleDramaToggle = (dramaId: string, max?: number) => {
    const current = selectedDramaIds;
    const isSelected = current.includes(dramaId);
    if (!isSelected && typeof max === "number" && max > 0 && current.length >= max) {
      alert(`最多只能選擇 ${max} 部劇集`);
      return;
    }
    const newSelection = current.includes(dramaId)
      ? current.filter((id) => id !== dramaId)
      : [...current, dramaId];
    setValue("config.drama_ids", newSelection, { shouldValidate: true });
  };

  if (!isNew && isLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton rows={4} variant="card" />
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <AdminPageHeader
        title={isNew ? "新增 Tab" : "編輯 Tab"}
        actions={
          <Button variant="outline" onClick={() => navigate("/home-layout")}>
            <ArrowLeft className="h-4 w-4" />
            返回
          </Button>
        }
      />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* 錯誤訊息 */}
        {saveError && (
          <AdminErrorAlert
            message={`儲存失敗：${saveError}`}
            onDismiss={() => setSaveError(null)}
          />
        )}

        {/* 基本資訊 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isNew && (
              <div className="space-y-2">
                <Label htmlFor="tab-id">Tab ID</Label>
                <Input
                  id="tab-id"
                  {...register("id")}
                  placeholder="例如：featured_tab（留空將自動生成）"
                  className={errors.id ? "border-destructive" : ""}
                  style={{ textTransform: "lowercase" }}
                />
                {errors.id && (
                  <p className="text-sm text-destructive">{errors.id.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  只能包含小寫字母、數字和底線，建立後無法修改
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="tab-title">
                標題 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tab-title"
                {...register("title")}
                placeholder="例如：精選內容"
                className={errors.title ? "border-destructive" : ""}
              />
              {errors.title && (
                <p className="text-sm text-destructive">{errors.title.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tab-icon">圖示（Emoji）</Label>
              <Input
                id="tab-icon"
                {...register("icon")}
                placeholder="例如：✨ 🏆 🎬"
              />
              <p className="text-xs text-muted-foreground">可選的 emoji 圖示</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tab-order">
                顯示順序 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="tab-order"
                type="number"
                {...register("order", { valueAsNumber: true })}
                placeholder="0"
                min={0}
                className={errors.order ? "border-destructive" : ""}
              />
              {errors.order && (
                <p className="text-sm text-destructive">{errors.order.message}</p>
              )}
              <p className="text-xs text-muted-foreground">數字越小越靠前顯示</p>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                id="tab-enabled"
                checked={enabledValue}
                onCheckedChange={(checked) => setValue("enabled", checked)}
              />
              <Label htmlFor="tab-enabled">啟用此 Tab</Label>
            </div>
          </CardContent>
        </Card>

        {/* Tab 類型設定 */}
        <Card>
          <CardHeader>
            <CardTitle>Tab 類型設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>
                Tab 類型 <span className="text-destructive">*</span>
              </Label>
              <Select
                value={tabType}
                onValueChange={(val) => setValue("type", val as HomeTabFormData["type"])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="featured">精選（可自訂輪播）</SelectItem>
                  <SelectItem value="ranking">排行榜（按播放次數排序）</SelectItem>
                  <SelectItem value="tag_filter">標籤篩選（根據標籤過濾劇集）</SelectItem>
                  <SelectItem value="custom">自訂列表（指定劇集）</SelectItem>
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-destructive">{errors.type.message}</p>
              )}
            </div>

            {/* 精選輪播自訂（最多 6） */}
            {tabType === "featured" && (
              <div className="space-y-2">
                <Label>輪播劇集（最多 6 部，可留空使用系統自動）</Label>
                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                  {dramasLoading ? (
                    <AdminLoadingSkeleton rows={3} height="h-8" />
                  ) : dramas && dramas.length > 0 ? (
                    <div className="space-y-2">
                      {dramas.map((drama) => (
                        <label
                          key={drama.id || drama.drama_id}
                          className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDramaIds.includes(drama.drama_id)}
                            onChange={() => handleDramaToggle(drama.drama_id, 6)}
                            className="h-4 w-4 rounded border-input"
                          />
                          <span className="text-sm text-foreground">
                            {drama.drama_title}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {drama.drama_id}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">尚無劇集資料</p>
                  )}
                </div>
                {errors.config?.drama_ids && (
                  <p className="text-sm text-destructive">
                    {errors.config.drama_ids.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  勾選的劇會固定放在輪播最前面；不足 6 部時，App 仍會自動補滿。
                </p>
              </div>
            )}

            {/* 標籤篩選配置 */}
            {tabType === "tag_filter" && (
              <div className="space-y-2">
                <Label>
                  選擇標籤 <span className="text-destructive">*</span>
                </Label>
                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                  {tagsLoading ? (
                    <AdminLoadingSkeleton rows={3} height="h-8" />
                  ) : tags && tags.length > 0 ? (
                    <div className="space-y-2">
                      {tags.map((tag) => (
                        <label
                          key={tag.id}
                          className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedTagIds.includes(tag.id)}
                            onChange={() => handleTagToggle(tag.id)}
                            className="h-4 w-4 rounded border-input"
                          />
                          <span className="text-sm text-foreground">
                            {tag.tw || tag.zh || tag.en || tag.id}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">尚無標籤資料</p>
                  )}
                </div>
                {errors.config?.tag_ids && (
                  <p className="text-sm text-destructive">
                    {errors.config.tag_ids.message}
                  </p>
                )}
              </div>
            )}

            {/* 自訂列表配置 */}
            {tabType === "custom" && (
              <div className="space-y-2">
                <Label>
                  選擇劇集 <span className="text-destructive">*</span>
                </Label>
                <div className="border rounded-lg p-4 max-h-64 overflow-y-auto">
                  {dramasLoading ? (
                    <AdminLoadingSkeleton rows={3} height="h-8" />
                  ) : dramas && dramas.length > 0 ? (
                    <div className="space-y-2">
                      {dramas.map((drama) => (
                        <label
                          key={drama.id}
                          className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDramaIds.includes(drama.drama_id)}
                            onChange={() => handleDramaToggle(drama.drama_id)}
                            className="h-4 w-4 rounded border-input"
                          />
                          <span className="text-sm text-foreground">
                            {drama.drama_title}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">尚無劇集資料</p>
                  )}
                </div>
                {errors.config?.drama_ids && (
                  <p className="text-sm text-destructive">
                    {errors.config.drama_ids.message}
                  </p>
                )}
              </div>
            )}

            {/* 限制數量（適用於所有類型） */}
            <div className="space-y-2">
              <Label htmlFor="config-limit">顯示數量限制</Label>
              <Input
                id="config-limit"
                type="number"
                {...register("config.limit", { valueAsNumber: true })}
                placeholder="留空表示不限制"
                min={1}
              />
              <p className="text-xs text-muted-foreground">最多顯示幾部劇（可選）</p>
            </div>
          </CardContent>
        </Card>

        {/* 操作按鈕 */}
        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/home-layout")}
          >
            取消
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            <Save className="h-4 w-4" />
            {mutation.isPending ? "儲存中..." : "儲存"}
          </Button>
        </div>
      </form>
    </AdminPage>
  );
}
